import { db } from './db.js';
import { net } from './net.js';
import { Emitter } from './utils.js';
import { createXmltvParser } from './epg-parse.js';

/* Gestor de la guía electrónica de programación (EPG).
   Formato en memoria: { epgId: [[start, stop, title, desc], ...] } ordenado por inicio. */

const EPG_TTL = 12 * 3600 * 1000;

export class EpgManager extends Emitter {
    constructor() {
        super();
        this.channels = {};
        this.updated = 0;
        this.loading = false;
        this.progress = 0;
        this.profileId = null;
        this.worker = null;
        this._loadPromise = null;
        // Guías de los proveedores añadidos al catálogo: id de proveedor → canales.
        this.extra = {};
    }

    reset() {
        this.extra = {};
        this.channels = {};
        this.updated = 0;
        this.loading = false;
        this.progress = 0;
        this._loadPromise = null;
    }

    async restore(profileId) {
        this.profileId = profileId;
        const cached = await db.get('epg', profileId);
        if (cached && cached.channels) {
            this.channels = cached.channels;
            this.updated = cached.updated || 0;
            this.emit('change');
            return true;
        }
        return false;
    }

    isStale() {
        return !this.updated || Date.now() - this.updated > EPG_TTL;
    }

    hasData() {
        return Object.keys(this.channels).length > 0 || Object.keys(this.extra).some((id) => Object.keys(this.extra[id]).length > 0);
    }

    /** Descarga y parsea el XMLTV. Devuelve true si se actualizó. */
    async load(profileId, url, { force = false } = {}) {
        if (!url) return false;
        if (this._loadPromise) return this._loadPromise;
        if (!force && this.profileId === profileId && this.hasData() && !this.isStale()) return false;

        this.profileId = profileId;
        this.loading = true;
        this.progress = 0;
        this.emit('loading', true);

        this._loadPromise = (async () => {
            try {
                const text = await net.fetchText(url, { timeout: 180000 });
                const result = await this._parse(text);
                this.channels = result.channels;
                this.updated = Date.now();
                await db.put('epg', profileId, { channels: this.channels, updated: this.updated, total: result.total });
                this.emit('change');
                return true;
            } finally {
                this.loading = false;
                this._loadPromise = null;
                this.emit('loading', false);
            }
        })();
        return this._loadPromise;
    }

    /**
     * Guía de un proveedor añadido al catálogo. Se suma a la del principal sin sustituirla: si un
     * canal está en las dos, vale la del principal. Usa su propia caché, con la misma caducidad.
     */
    async loadExtra(profileId, url, { force = false } = {}) {
        if (!url || !profileId) return false;
        const cached = force ? null : await db.get('epg', profileId);
        if (cached && cached.channels) {
            this.extra[profileId] = cached.channels;
            this.emit('change');
            if (Date.now() - (cached.updated || 0) < EPG_TTL) return false;
        }
        const text = await net.fetchText(url, { timeout: 180000 });
        const result = await this._parse(text);
        this.extra[profileId] = result.channels;
        await db.put('epg', profileId, { channels: result.channels, updated: Date.now(), total: result.total });
        this.emit('change');
        return true;
    }

    dropExtra(profileId) {
        if (!this.extra[profileId]) return;
        delete this.extra[profileId];
        this.emit('change');
    }

    _parse(text) {
        return new Promise((resolve, reject) => {
            // La app de Android carga la interfaz desde file://, donde el sistema puede negarse a crear workers (o el worker
            // falla al arrancar): en ese caso se analiza aquí, por trozos para no congelar la pantalla.
            const inline = () => this._parseInline(text).then(resolve, reject);
            if (typeof Worker === 'undefined') { inline(); return; }
            let worker;
            let started = false;
            try {
                worker = new Worker('dist/epg-worker.js');
            } catch (e) {
                inline();
                return;
            }
            worker.onmessage = (event) => {
                started = true;
                const msg = event.data;
                if (msg.type === 'progress') {
                    this.progress = msg.length ? msg.position / msg.length : 0;
                    this.emit('progress', this.progress);
                } else if (msg.type === 'done') {
                    worker.terminate();
                    resolve(msg);
                }
            };
            worker.onerror = (err) => {
                try { worker.terminate(); } catch (e) { /* ya terminado */ }
                if (!started) inline(); else reject(err);
            };
            worker.postMessage({ type: 'parse', text });
        });
    }

    /** Análisis en el hilo principal, en trozos de ~1,5 MB (cortados tras un </programme>) cediendo el control entre ellos. */
    async _parseInline(text) {
        const parser = createXmltvParser();
        const SIZE = 1500000;
        const END = '</programme>';
        let pos = 0;
        while (pos < text.length) {
            let end = text.length;
            if (text.length - pos > SIZE) {
                const cut = text.indexOf(END, pos + SIZE);
                end = cut === -1 ? text.length : cut + END.length;
            }
            parser.feed(text.slice(pos, end));
            pos = end;
            this.progress = text.length ? pos / text.length : 1;
            this.emit('progress', this.progress);
            await new Promise((r) => setTimeout(r, 0));
        }
        return parser.result();
    }

    programs(epgId) {
        if (!epgId) return [];
        if (this.channels[epgId]) return this.channels[epgId];
        const ids = Object.keys(this.extra);
        for (let i = 0; i < ids.length; i++) {
            if (this.extra[ids[i]][epgId]) return this.extra[ids[i]][epgId];
        }
        return [];
    }

    /** Primer inicio y último final de lo que hay en la guía (o null): para saber qué días se pueden consultar. */
    range() {
        let min = Infinity;
        let max = 0;
        const scan = (map) => {
            Object.keys(map || {}).forEach((k) => {
                const l = map[k];
                if (!l || !l.length) return;
                if (l[0][0] < min) min = l[0][0];
                const end = l[l.length - 1][1];
                if (end > max) max = end;
            });
        };
        scan(this.channels);
        Object.keys(this.extra).forEach((id) => scan(this.extra[id]));
        return min === Infinity ? null : { min, max };
    }

    /** Devuelve {now, next} para el canal, ya convertidos a objetos. */
    nowNext(epgId, at = Date.now()) {
        const list = this.programs(epgId);
        if (!list.length) return { now: null, next: null };
        // Búsqueda binaria del primer programa con stop > at.
        let lo = 0;
        let hi = list.length - 1;
        let idx = -1;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (list[mid][1] > at) {
                idx = mid;
                hi = mid - 1;
            } else {
                lo = mid + 1;
            }
        }
        if (idx === -1) return { now: null, next: null };
        const cur = list[idx];
        if (cur[0] <= at) {
            return { now: toProgram(cur), next: list[idx + 1] ? toProgram(list[idx + 1]) : null };
        }
        return { now: null, next: toProgram(cur) };
    }

    /** Lista de programas alrededor de ahora (algunos pasados, resto futuros) para la guía del canal. */
    schedule(epgId, { past = 6, future = 40 } = {}) {
        const list = this.programs(epgId);
        if (!list.length) return [];
        const now = Date.now();
        let idx = list.findIndex((p) => p[1] > now);
        if (idx === -1) idx = list.length;
        const from = Math.max(0, idx - past);
        return list.slice(from, idx + future).map(toProgram);
    }
}

export function toProgram(row) {
    return { start: row[0], stop: row[1], title: row[2], desc: row[3] };
}

export function programProgress(program, at = Date.now()) {
    if (!program) return 0;
    const total = program.stop - program.start;
    if (total <= 0) return 0;
    return Math.max(0, Math.min(100, ((at - program.start) / total) * 100));
}

export const epg = new EpgManager();
